<template>
  <v-dialog
    :model-value="show"
    @update:model-value="$emit('update:show', $event)"
    max-width="450px"
    :fullscreen="$vuetify.display.xs"
  >
    <v-card>
      <v-card-title>
        <div class="d-flex align-center justify-space-between">
          <Typography>Meeting Types</Typography>
          <v-btn variant="text" icon color="normal" @click="$emit('update:show', false)"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </div>
      </v-card-title>
      <v-divider class="mt-0"></v-divider>
      <template v-if="conferenceDay.meeting_types?.in_person">
        <v-card-item data-cy="meeting-type-in_person">
          <div class="in_person types-item">
            <div class="switcher">
              <Typography weight="600">In person</Typography>
              <v-switch
                color="primary"
                class="custom-input"
                density="compact"
                v-model="formData.inPerson"
              ></v-switch>
            </div>
            
            <v-text-field
                  v-model="formData.roomNumber"
                  label="Room"
                  color="primary"
                  class="custom-input mt-3"
                ></v-text-field>

          </div>
        </v-card-item>
      </template>
      <template v-if="$props.conferenceDay.meeting_types?.virtual">
        <v-card-item data-cy="meeting-type-virtual">
          <div class="vlink types-item">
            <div class="switcher">
              <Typography weight="600">Virtual Meeting</Typography>
              <v-switch
                color="primary"
                class="custom-input"
                density="compact"
                v-model="formData.virtual"
              ></v-switch>
            </div>
            <template v-if="formData.virtual">
              <v-btn-toggle
                v-model="formData.virtualType"
                density="compact"
                mandatory
                :style="`border: 1px solid rgba(0, 0, 0, 0.12); max-height: ${
                  $vuetify.display.smAndUp ? '31px' : '40px'
                }`"
              >
                <v-btn size="small" color="primary" value="url">
                  <span class="d-none d-sm-block mr-1">URL </span>
                  <v-icon size="large">mdi-link-variant</v-icon>
                </v-btn>
                <v-btn
                  size="small"
                  v-if="authStore.me.zoom_connected"
                  color="primary"
                  value="zoom"
                >
                  <span class="d-none d-sm-block mr-1"> ZOOM </span>

                  <ZoomVideo style="height: 20px; width: 20px" />
                </v-btn>
                <v-btn size="small" v-if="authStore.me.azure_teams" color="primary" value="teams"
                  ><span class="d-none d-sm-block mr-1">TEAMS</span>
                  <MSTeams style="height: 20px; width: 20px" />
                </v-btn>
                <v-btn
                  size="small"
                  v-if="authStore.me.google_calendar_connected && authStore.me.google_meet"
                  color="primary"
                  value="google"
                  ><span class="d-none d-sm-block mr-1">GOOGLE</span>
                  <GoogleMeet style="height: 20px; width: 20px" />
                </v-btn>
              </v-btn-toggle>
              <div v-if="formData.virtualType == 'url'">
                <v-text-field
                  v-model="formData.roomLink"
                  label="Room Link"
                  color="primary"
                  class="custom-input mt-3"
                ></v-text-field>
                <div
                  v-if="formData.roomLinkError && !formData.roomLink"
                  :style="{ color: pallette.danger }"
                >
                  {{ formData.roomLinkError }}
                </div>
              </div>
            </template>
          </div>
        </v-card-item>
      </template>
      <template v-if="$props.conferenceDay.meeting_types?.phone">
        <v-card-item data-cy="meeting-type-phone">
          <div class="p-teacher types-item">
            <div class="switcher">
              <Typography weight="600" color="secondary">Phone Teacher</Typography>
              <v-switch
                color="secondary"
                class="custom-input"
                density="compact"
                v-model="formData.phone"
              ></v-switch>
            </div>
            <v-text-field
              v-if="formData.phone"
              v-model="formData.phoneNumber"
              label="Phone Number"
            ></v-text-field>
          </div>
        </v-card-item>
      </template>
      <template v-if="$props.conferenceDay.meeting_types?.callback">
        <v-card-item data-cy="meeting-type-callback">
          <div class="p-teacher types-item">
            <div class="switcher">
              <Typography weight="600" color="secondary">Phone Parent</Typography>
              <v-switch
                color="secondary"
                class="custom-input"
                density="compact"
                v-model="formData.callback"
              ></v-switch>
            </div>
            <Typography color="secondary">Call Parent's Number</Typography>
          </div>
        </v-card-item>
      </template>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn :loading="formData.loading" variant="flat" @click="save"
          ><typography transform="capitalize" color="white" size="unset"
            >Save Changes</typography
          ></v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import type { ConferenceDay } from "@/api_types/teacher";
import Typography from "@/components/typography.vue";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthenticationStore } from "@/stores/authentication";
import { useConferenceStore } from "@/stores/teacher/conference";
import { pallette } from "@/utils/colors";
import { watch } from "vue";
import { reactive } from "vue";
import GoogleMeet from "@/assets/brand/googleMeet.svg";
import MSTeams from "@/assets/brand/microsoftTeams.svg";
import ZoomVideo from "@/assets/icons/zoom_video.svg";

const props = defineProps<{
  show: boolean;
  conferenceDay: ConferenceDay;
}>();

defineEmits<{
  (e: 'update:show', ...args: any[]): void;
  (e: 'update:meetingTypes', ...args: any[]): void
}>();

const authStore = useAuthenticationStore();
const confStore = useConferenceStore();

const formData = reactive({
  phone: props.conferenceDay.teacher_meeting_types?.phone || false,
  inPerson: props.conferenceDay.teacher_meeting_types?.in_person || false,
  callback: props.conferenceDay.teacher_meeting_types?.callback || false,
  virtual: props.conferenceDay.teacher_meeting_types?.virtual || false,
  phoneNumber: props.conferenceDay.phone_number || "",
  roomLink: props.conferenceDay.virtual_room || "",
  virtualType: props.conferenceDay.teacher_meeting_types?.virtual_type || "url",
  roomNumber: props.conferenceDay.room || authStore.me.room_assignment || "",
  roomLinkError: "",
  loading: false
});

watch(
  () => props.show,
  () => {
    if (props.show) {
      formData.phone = props.conferenceDay.teacher_meeting_types?.phone || false;
      formData.inPerson = props.conferenceDay.teacher_meeting_types?.in_person || false;
      formData.callback = props.conferenceDay.teacher_meeting_types?.callback || false;
      formData.virtual = props.conferenceDay.teacher_meeting_types?.virtual || false;
      formData.roomLink = props.conferenceDay.virtual_room || "";
      formData.phoneNumber = props.conferenceDay.phone_number || "";
      formData.virtualType = props.conferenceDay.teacher_meeting_types?.virtual_type || "url";
      formData.roomNumber = props.conferenceDay.room || authStore.me.room_assignment || "";
    }
  }
);

const save = async () => {
  if (formData.virtual && formData.virtualType == "url" && !formData.roomLink) {
    formData.roomLinkError = "Room Link is required when virtual type is URL";
    return;
  }
  formData.roomLinkError = "";
  formData.loading = true;
  const res = await FetchService.fetchData(
    `/meeting-types/${props.conferenceDay.conference_days_id}/${authStore.me.uid}`,
    "post",
    {
      teacher_meeting_types: {
        phone: formData.phone,
        in_person: formData.inPerson,
        callback: formData.callback,
        virtual: formData.virtual,
        virtual_type: formData.virtualType
      },
      virtualLink: formData.roomLink,
      phoneNumber: formData.phoneNumber,
      room: formData.roomNumber,
    }
  );
  const conferenceDay = confStore.selected?.conference_days.find(
    (cd) => cd.conference_days_id === props.conferenceDay.conference_days_id
  );

  if (res.teacher_meeting_types && conferenceDay) {
    conferenceDay.teacher_meeting_types = res.teacher_meeting_types as any;
    conferenceDay.phone_number = res.phone_number;
    conferenceDay.virtual_room = res.virtual_room;
    conferenceDay.room = res.room;
  } else {
    useAlertsStore().addAlert("error", "Failed to update teacher meeting types", 7);
  }
  // update the meeting types
  formData.loading = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.switcher {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .custom-input {
    max-width: fit-content;
  }
}
.types-item {
  padding: 0 5px;
  width: 100%;
}
</style>
